import type { BaseServiceOptions, Pagination } from '@/core/types'
import {
  type TBundle,
  bundleServiceHandlers,
  serviceConfig,
  moduleServiceHandlers,
  TBundles,
  TCoilPayload,
  TProject,
} from '../../..'
import { useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { useCoilsStore } from '../../store/coils'

const { model } = serviceConfig.bundles

export const useOrderQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, filters, enableQuery, modelId } = options

  const coilsStore = useCoilsStore()
  const { errorHttpNotification } = useNotification()
  const { successWithActionNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TBundle>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TBundle>(
    (payload) => bundleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, warehouse) => serviceHandlers.deleteRecord(warehouse),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TBundle, AxiosError, TBundle>(
    (payload: TBundle) => bundleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TBundle, AxiosError, TBundle>(
    (payload: TBundle) => bundleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TBundles, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords, modelId],
    queryFn: () =>
      bundleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
  })

  const addCoils = useMutation<TProject, AxiosError, TCoilPayload>(
    (payload: TCoilPayload) =>
      moduleServiceHandlers.addCoils(payload, { modelId }),
    {
      onError: (error) => errorHttpNotification(error),

      onSuccess: (data) => {
        coilsStore.setCoils(data.coils ?? []),
          successWithActionNotification('ADDED', 'coils')
      },
    },
  )

  const printRecord = async (id: number, type: string) =>
    await moduleServiceHandlers.printRecord(id, type)

  return {
    loadRecords,
    addRecord,
    editRecord,
    addCoils,
    deleteRecord,
    printRecord,
  }
}
